import React, { createContext, useContext, useState, useEffect } from 'react';
import useAuthStore from '../store/authStore';
import configs from '../config';

const TenantContext = createContext();

/**
 * Utility function to extract tenant identifier from URL path
 */
export function getTenantIdentifier() {
  if (typeof window !== 'undefined') {
    // Prefer explicit tenant-prefixed routes first (e.g., /:tenant/login, /:tenant/register, /t/:slug/*)
    // so stale localStorage cannot override the chosen workspace on auth routes.
    const pathname = window.location.pathname || '/';
    const segments = pathname.split('/').filter(Boolean);
    const reserved = new Set([
      'login',
      'register',
      'forgot-password',
      'reset-password',
      'admin',
      'owner',
      'dashboard',
      'jobs',
      'proposals',
      'messages',
      'settings',
      'profile',
    ]);
    if (segments[0] === 't' && segments[1]) {
      return segments[1];
    }
    const first = segments[0];
    if (first && !reserved.has(first) && segments.length > 1) {
      // If app uses /:tenant/... pattern, only accept when there is a subpath
      return first;
    }

    // Fallback to persisted tenant from previous login
    const storedTenant = localStorage.getItem('tenant-slug');
    if (storedTenant) {
      return storedTenant;
    }
  }
  
  return null;
}

/**
 * Tenant Provider Component
 */
export function TenantProvider({ children }) {
  const { user, isAuthenticated } = useAuthStore();
  const [tenant, setTenant] = useState(null);
  const [tenantConfig, setTenantConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Apply tenant branding (CSS variables + favicon)
  useEffect(() => {
    if (tenant?.branding?.primaryColor) {
      document.documentElement.style.setProperty('--tenant-primary', tenant.branding.primaryColor);
    } else {
      document.documentElement.style.removeProperty('--tenant-primary');
    }
    if (tenant?.branding?.favicon) {
      const link = document.querySelector("link[rel~='icon']") || document.createElement('link');
      link.rel = 'icon';
      link.href = tenant.branding.favicon;
      document.head.appendChild(link);
    }
  }, [tenant?.branding]);

  // Initialize tenant from user data or subdomain
  useEffect(() => {
    const initializeTenant = async () => {
      if (!isAuthenticated || !user) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Get tenant identifier
        const tenantSlug = getTenantIdentifier();

        // If user has tenantId, fetch tenant details for current tenant (no ID in path)
        if (user.tenantId) {
          try {
            const response = await fetch(
              `${configs.BACKEND_URL}/api/tenant/current`,
              {
                method: 'GET',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                  ...(tenantSlug && { 'X-Tenant-Subdomain': tenantSlug }),
                },
              }
            );

            if (response.ok) {
              const tenantData = await response.json();
              setTenant(tenantData.data || tenantData);
            } else {
              console.warn('Failed to fetch tenant details');
            }
          } catch (err) {
            console.error('Error fetching tenant:', err);
          }

          // Fetch tenant configuration (admin/owner only — requires tenant_config:read permission)
          const role = (user.role || '').toLowerCase();
          if (role === 'admin' || role === 'owner') {
            try {
              const configResponse = await fetch(
                `${configs.BACKEND_URL}/api/tenant/config`,
                {
                  method: 'GET',
                  credentials: 'include',
                  headers: {
                    'Content-Type': 'application/json',
                    ...(tenantSlug && { 'X-Tenant-Subdomain': tenantSlug }),
                  },
                }
              );

              if (configResponse.ok) {
                const configData = await configResponse.json();
                setTenantConfig(configData.data || configData);
              }
            } catch {
              // Silent — config is only needed for admin settings panels
            }
          }
        } else if (tenantSlug) {
          // If no tenantId in user but we have a slug, try to get current tenant
          try {
            const response = await fetch(
              `${configs.BACKEND_URL}/api/tenant/current`,
              {
                method: 'GET',
                credentials: 'include',
                headers: {
                  'Content-Type': 'application/json',
                  'X-Tenant-Subdomain': tenantSlug,
                },
              }
            );

            if (response.ok) {
              const tenantData = await response.json();
              setTenant(tenantData.data || tenantData);
            }
          } catch (err) {
            console.error('Error fetching tenant by slug:', err);
          }
        }
      } catch (err) {
        console.error('Error initializing tenant:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    initializeTenant();
  }, [isAuthenticated, user]);

  // Function to get tenant headers for API calls
  const getTenantHeaders = () => {
    const tenantSlug = getTenantIdentifier();
    const headers = {
      'Content-Type': 'application/json',
    };

    if (tenantSlug) {
      headers['X-Tenant-Subdomain'] = tenantSlug;
    }

    if (tenant?._id) {
      headers['X-Tenant-Id'] = tenant._id;
    }

    return headers;
  };

  const value = {
    tenant,
    tenantConfig,
    loading,
    error,
    getTenantHeaders,
    refreshTenant: async () => {
      // Force refresh tenant data
      if (user?.tenantId) {
        try {
          const tenantSlug = getTenantIdentifier();
          const response = await fetch(
            `${configs.BACKEND_URL}/api/tenant/current`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                ...(tenantSlug && { 'X-Tenant-Subdomain': tenantSlug }),
              },
            }
          );

          if (response.ok) {
            const tenantData = await response.json();
            setTenant(tenantData.data || tenantData);
          }
        } catch (err) {
          console.error('Error refreshing tenant:', err);
        }
      }
    },
    refreshConfig: async () => {
      // Force refresh tenant configuration (admin/owner only)
      const role = (user?.role || '').toLowerCase();
      if (user?.tenantId && (role === 'admin' || role === 'owner')) {
        try {
          const tenantSlug = getTenantIdentifier();
          const configResponse = await fetch(
            `${configs.BACKEND_URL}/api/tenant/config`,
            {
              method: 'GET',
              credentials: 'include',
              headers: {
                'Content-Type': 'application/json',
                ...(tenantSlug && { 'X-Tenant-Subdomain': tenantSlug }),
              },
            }
          );

          if (configResponse.ok) {
            const configData = await configResponse.json();
            setTenantConfig(configData.data || configData);
          }
        } catch {
          // Silent
        }
      }
    },
  };

  return (
    <TenantContext.Provider value={value}>
      {children}
    </TenantContext.Provider>
  );
}

/**
 * Hook to use tenant context
 */
export function useTenant() {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}

export default TenantContext;
